// Foto aus Kamera/Galerie → JPEG mit ~800 px längster Kante + Qualitätswerte.
//
// Verkleinern im Browser spart Datenvolumen beim Kunden und Speicher am
// Server, und es entfernt nebenbei alle EXIF-Daten (Canvas schreibt keine).
// Die Ausrichtung (Hochformat vom Handy) berücksichtigen aktuelle Browser
// beim Zeichnen eines <img> automatisch.

import { analyzePixels, targetSize } from "./qualitaet.ts";
import type { ProcessedImage } from "./store.ts";

const JPEG_QUALITY = 0.82;

/** Spätestens dann Fehlermeldung statt endlosem "Foto wird vorbereitet". */
const TIMEOUT_MS = 30_000;

// Bewusst onload statt img.decode(): decode() löst in einem verborgenen Tab
// nie auf (am 07.10.2026 im Test beobachtet). Auf dem Handy ist die Seite
// beim Zurückkehren aus der Kamera-App kurz verborgen – genau dann käme das Foto.
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error("Zeitüberschreitung beim Laden des Fotos")), TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("Foto nicht lesbar"));
    };
    img.src = url;
  });
}

export async function processImage(file: File): Promise<ProcessedImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { width, height } = targetSize(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Kein Canvas");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);
    const { brightness, blurScore } = analyzePixels(ctx.getImageData(0, 0, width, height).data, width, height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob"))), "image/jpeg", JPEG_QUALITY),
    );
    // iOS gibt Canvas-Speicher sonst spät frei; bei vielen Fotos stürzt Safari sonst ab.
    canvas.width = 0;
    canvas.height = 0;
    return { blob, brightness, blurScore };
  } finally {
    URL.revokeObjectURL(url);
  }
}
